import { useNavigationParams } from './history'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import type { DrawerTarget } from '../ObjectDrawer'
import type { RecordTablePayload } from '../RecordTable'
import { manualMovementHref } from '../manualMovement'
import { formatOfficerValue } from '../../lib/officerFormat'
import { useContextualNavigation, useRead } from './useRead'
import {
  chainOf,
  focusForTarget,
  parseFocus,
  serializeFocus,
  targetForFocus,
  type Focus,
} from '../fund/focus'
import FundOverviewTab from '../fund/FundOverviewTab'
import type { MatrixRow } from '../fund/fundArea'
import OpenItems from './OpenItems'
import SourceDocumentList from '../documents/SourceDocumentList'
import {
  recordContents,
  recordHref,
  recordQuery,
  type ContextWork,
  type RecordContext,
  type ViewScope,
  type WorkCard,
} from './context'
import { ErrorState, Skeleton } from '../state'
import './subchain-overview.css'

type Target = DrawerTarget
type FactValue = string | number | boolean | null | undefined

type RecordRow = { id: string; state: 'proposed' | 'recorded' | 'evidenced' }

function idsFor(card: WorkCard, kind: string): Set<string> {
  return new Set(card.members.filter((member) => member.kind === kind).map((member) => member.id))
}

function recordedCounts(rows: readonly RecordRow[]) {
  return rows.reduce(
    (counts, row) => {
      counts[row.state] += 1
      return counts
    },
    { recorded: 0, evidenced: 0, proposed: 0 }
  )
}

function tableContainsCard(record: RecordTablePayload, card: WorkCard): boolean {
  const positions = idsFor(card, 'position')
  if (positions.size) {
    return (
      record.positions.some((row) => positions.has(row.id)) ||
      record.segments.some(
        (segment) =>
          positions.has(segment.position_id) ||
          segment.position_ids.some((positionId) => positions.has(positionId))
      )
    )
  }
  const members = new Set(card.members.map((member) => member.id))
  return (
    record.entities.some((row) => members.has(row.id)) ||
    record.instruments.some((row) => members.has(row.id)) ||
    record.positions.some((row) => members.has(row.id)) ||
    record.trades.some((row) => members.has(row.id))
  )
}

/** Keep a wider navigation response from turning a subchain page into a fund page. */
function selectedTable(record: RecordTablePayload, card: WorkCard): RecordTablePayload {
  const memberPositions = idsFor(card, 'position')
  const memberInstruments = idsFor(card, 'instrument')
  const memberEntities = idsFor(card, 'entity')
  const memberTrades = idsFor(card, 'trade')
  const identity = card.identity
  const segments = record.segments.filter((segment) => {
    if (segment.state === 'proposed') return false
    if (identity.kind === 'subchain') {
      const positionMatch =
        memberPositions.has(segment.position_id) ||
        segment.position_ids.some((positionId) => memberPositions.has(positionId))
      return (
        positionMatch ||
        (memberPositions.size === 0 &&
          segment.holder_id === identity.holder_id &&
          segment.instrument_id === identity.instrument_id &&
          segment.issuer_id === identity.issuer_id)
      )
    }
    if (identity.kind === 'instrument')
      return (
        segment.instrument_id === identity.instrument_id && segment.issuer_id === identity.issuer_id
      )
    return segment.holder_id === identity.entity_id || segment.issuer_id === identity.entity_id
  })
  const positionIds = new Set(memberPositions)
  const instrumentIds = new Set(memberInstruments)
  const entityIds = new Set(memberEntities)
  for (const segment of segments) {
    positionIds.add(segment.position_id)
    for (const positionId of segment.position_ids) positionIds.add(positionId)
    if (segment.instrument_id) instrumentIds.add(segment.instrument_id)
    entityIds.add(segment.holder_id)
    if (segment.issuer_id) entityIds.add(segment.issuer_id)
  }
  if (identity.holder_id) entityIds.add(identity.holder_id)
  if (identity.issuer_id) entityIds.add(identity.issuer_id)
  if (identity.entity_id) entityIds.add(identity.entity_id)
  if (identity.instrument_id) instrumentIds.add(identity.instrument_id)
  const positions = record.positions.filter(
    (row) => row.state !== 'proposed' && positionIds.has(row.id)
  )
  const instruments = record.instruments.filter(
    (row) => row.state !== 'proposed' && instrumentIds.has(row.id)
  )
  const entities = record.entities.filter(
    (row) => row.state !== 'proposed' && entityIds.has(row.id)
  )
  const trades = record.trades.filter((row) => {
    if (row.state === 'proposed') return false
    if (memberTrades.has(row.id)) return true
    if (row.position_id && positionIds.has(row.position_id)) return true
    return (
      identity.kind === 'instrument' &&
      !row.position_id &&
      !!row.instrument_id &&
      instrumentIds.has(row.instrument_id)
    )
  })
  const objects = [...entities, ...instruments, ...positions, ...trades]
  return {
    ...record,
    counts: recordedCounts(objects),
    segments,
    entities,
    instruments,
    positions,
    trades,
    dropped: record.dropped?.filter((row) => positionIds.has(row.id)),
  }
}

function selectedMatrix(matrix: unknown[], table: RecordTablePayload): MatrixRow[] {
  const ids = new Set([
    ...table.entities.map((row) => row.id),
    ...table.instruments.map((row) => row.id),
    ...table.positions.map((row) => row.id),
    ...table.trades.map((row) => row.id),
  ])
  return matrix.filter((row): row is MatrixRow => {
    if (!row || typeof row !== 'object') return false
    const objectId = (row as { object_id?: unknown }).object_id
    return typeof objectId === 'string' && ids.has(objectId)
  })
}

function selectedWork(work: ContextWork, card: WorkCard): ContextWork {
  const requirementIds = new Set([...card.evidence_requirement_ids, ...card.chase_requirement_ids])
  const requirements = work.requirements.filter((requirement) => requirementIds.has(requirement.id))
  const memberIds = new Set(card.members.map((member) => member.id))
  const tasks = work.tasks.filter(
    (task) =>
      (card.close_task_ids ?? []).includes(task.id) ||
      (!!task.target_id && memberIds.has(task.target_id)) ||
      (!!task.target_id && requirementIds.has(task.target_id))
  )
  const documents = work.documents.filter((document) =>
    card.document_ids.includes(document.reference.document_id)
  )
  const queues = work.queues.map((queue) => {
    const inQueue = queue.card_ids.includes(card.id)
    const queueRequirementIds =
      queue.name === 'evidence'
        ? card.evidence_requirement_ids
        : queue.name === 'chase'
          ? card.chase_requirement_ids
          : []
    const requestCount = requirements
      .filter((requirement) => queueRequirementIds.includes(requirement.id))
      .reduce((count, requirement) => count + requirement.request_ids.length, 0)
    return {
      ...queue,
      card_ids: inQueue ? [card.id] : [],
      counts: {
        card_count: inQueue ? 1 : 0,
        proposal_count: inQueue ? card.book_proposal_ids.length : 0,
        requirement_count: inQueue ? queueRequirementIds.length : 0,
        request_count: inQueue ? requestCount : 0,
      },
    }
  })
  return {
    ...work,
    cards: [card],
    queues,
    documents,
    requirements,
    tasks,
  }
}

type CanonicalObject = {
  anchor: { kind: 'entity' | 'instrument' | 'position' | 'trade'; id: string }
  label?: string | null
  record_state: string
  fields?: Record<string, unknown>
}

type WorkWithObjects = ContextWork & { objects?: readonly CanonicalObject[] }

function canonicalObjects(work: ContextWork, card: WorkCard): CanonicalObject[] {
  const members = new Set(card.members.map((member) => member.kind + ':' + member.id))
  return ((work as WorkWithObjects).objects ?? []).filter(
    (object) =>
      members.has(object.anchor.kind + ':' + object.anchor.id) &&
      object.record_state === 'confirmed'
  )
}

function fieldLabel(key: string): string {
  return key
    .replace(/_id$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (value) => value.toUpperCase())
}

function fieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not recorded'
  if (Array.isArray(value)) return value.map(fieldValue).join(', ')
  if (typeof value === 'object')
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => fieldLabel(key) + ': ' + fieldValue(item))
      .join('; ')
  return formatOfficerValue(value)
}

function objectName(object: CanonicalObject): string {
  const fields = object.fields ?? {}
  const fieldName = fields.canonical_name ?? fields.name
  return (
    object.label ??
    (typeof fieldName === 'string' ? fieldName : null) ??
    fieldLabel(object.anchor.kind)
  )
}

function CanonicalObjectFacts({
  objects,
  scope,
}: {
  objects: readonly CanonicalObject[]
  scope: ViewScope
}) {
  const hasPosition = objects.some((object) => object.anchor.kind === 'position')
  return (
    <section className="subchain-overview__facts" data-testid="subchain-object-facts">
      <div className="subchain-overview__section-head">
        <h2>Current Record facts</h2>
      </div>
      {objects.map((object) => {
        const fields = Object.entries(object.fields ?? {}).filter(
          ([key]) =>
            key !== 'id' && key !== 'created_at' && key !== 'updated_at' && !key.endsWith('_id')
        )
        const focus = object.anchor.kind + ':' + object.anchor.id
        return (
          <article
            key={object.anchor.kind + ':' + object.anchor.id}
            className="subchain-overview__object"
            data-testid={'subchain-object-' + object.anchor.kind}
          >
            <div className="subchain-overview__section-head">
              <h3>{objectName(object)}</h3>
              <Link to={recordHref(scope, { focus })}>Open in Record →</Link>
              {scope.fund_id &&
              (object.anchor.kind === 'position' || object.anchor.kind === 'instrument') ? (
                <Link
                  to={manualMovementHref(scope.fund_id, {
                    kind: object.anchor.kind,
                    id: object.anchor.id,
                  })}
                >
                  Add movement
                </Link>
              ) : null}
            </div>
            {fields.length ? (
              <dl>
                {fields.map(([key, value]) => (
                  <div key={key}>
                    <dt>{fieldLabel(key)}</dt>
                    <dd>{fieldValue(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </article>
        )
      })}
      {!hasPosition ? (
        <p className="subchain-overview__no-position">
          No recorded position is present in this context.
        </p>
      ) : null}
    </section>
  )
}

function text(value: FactValue): string {
  if (value === null || value === undefined || value === '') return 'Not recorded'
  return formatOfficerValue(value)
}

function targetLabel(target: Target, table: ReturnType<typeof recordContents>): string {
  if (target.type === 'entity')
    return table.entities.find((row) => row.id === target.id)?.name ?? 'Entity'
  if (target.type === 'instrument')
    return table.instruments.find((row) => row.id === target.id)?.name ?? 'Instrument'
  if (target.type === 'position') {
    const row = table.positions.find((item) => item.id === target.id)
    return row?.instrument_name ?? row?.holder_name ?? 'Position'
  }
  const row = table.trades.find((item) => item.id === target.id)
  return row?.transaction_type?.replace(/_/g, ' ') ?? 'Movement'
}

function CurrentFacts({
  target,
  table,
  scope,
}: {
  target: Target | null
  table: ReturnType<typeof recordContents>
  scope: ViewScope
}) {
  if (!target) return null
  const rows: { label: string; value: FactValue }[] = []
  if (target.type === 'entity') {
    const row = table.entities.find((item) => item.id === target.id)
    if (row)
      rows.push(
        { label: 'Name', value: row.name },
        { label: 'Legal form', value: row.legal_form },
        { label: 'Jurisdiction', value: row.jurisdiction },
        { label: 'Registered address', value: row.registered_address }
      )
  } else if (target.type === 'instrument') {
    const row = table.instruments.find((item) => item.id === target.id)
    if (row)
      rows.push(
        { label: 'Instrument', value: row.name },
        { label: 'Issuer', value: row.issuer_name },
        { label: 'Issued', value: row.outstanding_units },
        { label: 'Currency', value: row.currency },
        { label: 'Issuance date', value: row.issuance_date }
      )
  } else if (target.type === 'position') {
    const row = table.positions.find((item) => item.id === target.id)
    if (row)
      rows.push(
        { label: 'Holder', value: row.holder_name },
        { label: 'Instrument', value: row.instrument_name },
        { label: 'Issuer', value: row.issuer_name },
        { label: 'Recorded balance', value: row.units_held },
        { label: 'Currency', value: row.units_currency },
        { label: 'Balance date', value: row.as_of }
      )
  } else {
    const row = table.trades.find((item) => item.id === target.id)
    if (row)
      rows.push(
        { label: 'Movement', value: row.transaction_type?.replace(/_/g, ' ') },
        { label: 'Units', value: row.units_delta },
        { label: 'Amount', value: row.amount },
        { label: 'Currency', value: row.currency },
        { label: 'Effective date', value: row.effective_date }
      )
  }
  const focus = target.type === 'position' ? 'segment:' + target.id : target.type + ':' + target.id
  return (
    <section className="subchain-overview__facts" data-testid="subchain-current-facts">
      <div className="subchain-overview__section-head">
        <h2>Current Record facts</h2>
        <Link to={recordHref(scope, { focus })}>Open in Record →</Link>
        {scope.fund_id && (target.type === 'position' || target.type === 'instrument') ? (
          <Link to={manualMovementHref(scope.fund_id, { kind: target.type, id: target.id })}>
            Add movement
          </Link>
        ) : null}
      </div>
      <p className="subchain-overview__selected">{targetLabel(target, table)}</p>
      <dl>
        {rows.map((row) => (
          <div key={row.label}>
            <dt>{row.label}</dt>
            <dd>{text(row.value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

export default function SubchainOverview() {
  const { id } = useParams<{ id: string }>()
  const [params, setParams] = useNavigationParams()
  const capability = useContextualNavigation()
  const scope = useMemo<ViewScope>(
    () => ({
      kind: 'subchain',
      subchain_id: id ?? null,
      fund_id: params.get('fund'),
      client_id: params.get('client'),
    }),
    [id, params]
  )
  const [refresh, setRefresh] = useState(0)
  const read = useRead<RecordContext>(id ? recordQuery(scope) : null, refresh)
  const value = read.value
  const card = value?.work.cards.find((item) => item.id === id)
  const sourceRecord = useMemo(() => {
    if (!value || !card) return null
    const candidates = value.records.filter(
      (record) => !card.fund_ids.length || card.fund_ids.includes(record.fund_id)
    )
    return candidates.find((record) => tableContainsCard(record.record, card)) ?? null
  }, [card, value])
  const table = useMemo(
    () => (sourceRecord && card ? selectedTable(sourceRecord.record, card) : null),
    [card, sourceRecord]
  )
  const scopedValue = useMemo(() => {
    if (!value || !sourceRecord || !table) return null
    return {
      ...value,
      records: [
        {
          ...sourceRecord,
          record: table,
          matrix: selectedMatrix(sourceRecord.matrix, table) as unknown as Record<
            string,
            unknown
          >[],
        },
      ],
    }
  }, [sourceRecord, table, value])
  const contents = useMemo(() => (scopedValue ? recordContents(scopedValue) : null), [scopedValue])
  const work = useMemo(() => (value && card ? selectedWork(value.work, card) : null), [card, value])
  const objects = useMemo(
    () => (value && card ? canonicalObjects(value.work, card) : []),
    [card, value]
  )
  const chain = useMemo(() => (table ? chainOf(table.segments) : []), [table])
  const knownHead = useMemo(
    () =>
      table?.entities.find((entity) => entity.is_fund_root && entity.state !== 'proposed')?.id ??
      null,
    [table]
  )
  const [focus, setFocus] = useState<Focus | null>(() => parseFocus(params.get('focus')))
  const selectionCleared = params.get('selection') === 'none'
  const initialTarget = useMemo<Target | null>(() => {
    if (selectionCleared && !focus) return null
    if (focus) {
      const focused = targetForFocus(chain, focus, knownHead)
      if (focused) return focused
    }
    const position = card?.members.find((member) => member.kind === 'position')
    if (position) return { type: 'position', id: position.id }
    if (card?.identity.kind === 'instrument' && card.identity.instrument_id)
      return { type: 'instrument', id: card.identity.instrument_id }
    if (card?.identity.kind === 'entity' && card.identity.entity_id)
      return { type: 'entity', id: card.identity.entity_id }
    return null
  }, [card, chain, focus, knownHead, selectionCleared])
  const [selected, setSelected] = useState<Target | null>(null)

  useEffect(() => {
    setFocus(parseFocus(params.get('focus')))
  }, [params])

  useEffect(() => {
    setSelected(initialTarget)
  }, [id, initialTarget])

  if (capability.loading || read.loading)
    return (
      <main className="subchain-overview" role="status">
        <Skeleton kind="cards" n={1} height={280} />
      </main>
    )
  if (capability.error)
    return (
      <main className="subchain-overview">
        <ErrorState what="subchain navigation" error={capability.error} />
      </main>
    )
  if (!capability.value?.enabled)
    return (
      <main className="subchain-overview">
        <h1>Subchain overview unavailable</h1>
        <p>Open the fund record to continue.</p>
      </main>
    )
  if (read.error)
    return (
      <main className="subchain-overview">
        <ErrorState
          what="subchain overview"
          error={read.error}
          onRetry={() => setRefresh((value) => value + 1)}
        />
      </main>
    )
  if (!id || !value || !card || !work || (!objects.length && (!table || !contents || !scopedValue)))
    return (
      <main className="subchain-overview">
        <ErrorState
          what="subchain overview"
          error={new Error('This exact subchain is unavailable')}
          onRetry={() => setRefresh((value) => value + 1)}
        />
      </main>
    )

  const fundId = scope.fund_id ?? table?.fund_id ?? value.fund_ids[0] ?? null
  const parentFund = fundId
    ? '/v2/funds/' +
      encodeURIComponent(fundId) +
      (scope.client_id ? '?client=' + encodeURIComponent(scope.client_id) : '')
    : '/v2/funds'
  const matrix = (scopedValue?.records[0]?.matrix ?? []) as unknown as MatrixRow[]
  const open = (target: Target) => {
    const tradePosition =
      target.type === 'trade'
        ? table?.trades.find((trade) => trade.id === target.id)?.position_id
        : null
    const next = focusForTarget(chain, target, tradePosition, knownHead)
    setFocus(next)
    setSelected(target)
    const nextParams = new URLSearchParams(params)
    nextParams.delete('selection')
    if (next) nextParams.set('focus', serializeFocus(next))
    else nextParams.delete('focus')
    setParams(nextParams)
  }
  const clear = () => {
    setSelected(null)
    setFocus(null)
    const nextParams = new URLSearchParams(params)
    nextParams.delete('focus')
    nextParams.set('selection', 'none')
    setParams(nextParams)
  }
  const docs = work.documents.map((document) => document.reference)
  return (
    <main className="subchain-overview fa" data-testid="subchain-overview">
      <header className="subchain-overview__header">
        <nav aria-label="Subchain navigation">
          <Link to={parentFund}>Parent fund →</Link>
          <Link to={recordHref(scope)}>Open subchain Record →</Link>
        </nav>
        <h1>{card.label}</h1>
      </header>
      <OpenItems work={work} scope={scope} />
      {table && contents ? (
        <>
          <FundOverviewTab
            contextual
            fundId={fundId ?? undefined}
            table={table}
            matrix={matrix}
            walk={[]}
            focus={focus}
            onOpen={open}
            onClearFocus={clear}
            onJump={open}
          />
          <CurrentFacts target={selected} table={contents} scope={scope} />
        </>
      ) : (
        <CanonicalObjectFacts objects={objects} scope={scope} />
      )}
      {docs.length ? (
        <section className="subchain-overview__documents">
          <h2>Documents</h2>
          <SourceDocumentList documents={docs} />
        </section>
      ) : null}
    </main>
  )
}
